import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import ShareButtons from "@/components/ShareButtons";
import { Clock, Users, ArrowLeft, ArrowRight, MapPin, Flame, Lightbulb, BookOpen } from "lucide-react";

const RecipeDetail = () => {
    const { slug } = useParams();
    const [recipe, setRecipe] = useState(null);
    const [related, setRelated] = useState([]);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        setRecipe(null);
        setError("");
        window.scrollTo(0, 0);
        api.get(`/recipes/${slug}`)
            .then(async (r) => {
                setRecipe(r.data);
                const [allR, prodR] = await Promise.all([
                    api.get("/recipes"),
                    api.get("/products").catch(() => ({ data: { products: [] } })),
                ]);
                setProducts(prodR.data.products || []);
                const rel = allR.data.filter(
                    (x) => x.id !== r.data.id &&
                    ((x.categories || []).some((c) => (r.data.categories || []).includes(c)) ||
                        x.origin === r.data.origin)
                ).slice(0, 3);
                setRelated(rel);
            })
            .catch(() => setError("Recipe not found"));
    }, [slug]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="recipe-not-found">
                <p className="font-script text-3xl text-hl-secondary">oh no —</p>
                <h1 className="font-serif text-5xl mt-3 text-hl-brown">We couldn't find that recipe</h1>
                <button
                    onClick={() => navigate("/recipes")}
                    className="mt-10 px-7 py-3 bg-hl-primary text-hl-bg hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                >
                    Back to all recipes
                </button>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center text-hl-muted font-serif italic text-2xl" data-testid="recipe-loading">
                Loading the story...
            </div>
        );
    }

    const total = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);

    return (
        <article data-testid="recipe-detail" className="pb-28">
            {/* HERO */}
            <div className="relative h-[80vh] min-h-[560px] overflow-hidden">
                <img
                    src={recipe.hero_image}
                    alt={recipe.title}
                    className="absolute inset-0 w-full h-full object-cover hl-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-hl-brown/95 via-hl-brown/35 to-hl-brown/15" />
                <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-16">
                    <Link
                        to="/recipes"
                        data-testid="recipe-back"
                        className="inline-flex items-center gap-2 text-hl-bg/80 hover:text-hl-bg text-[11px] uppercase tracking-[0.3em] mb-7 w-fit"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All recipes
                    </Link>
                    <p className="font-script text-[#E8D9B5] text-3xl md:text-4xl mb-3">
                        a dish from {recipe.origin.toLowerCase()} —
                    </p>
                    {recipe.featured && recipe.episode ? (
                        <div
                            data-testid="recipe-episode-badge"
                            className="inline-flex items-center gap-3 mb-4 px-4 py-1.5 bg-hl-bg/10 border border-[#E8D9B5]/40 backdrop-blur-sm rounded-sm w-fit"
                        >
                            <span className="font-script text-[#E8D9B5] text-xl leading-none">ep.</span>
                            <span className="font-serif text-hl-bg text-sm tracking-[0.22em] leading-none">
                                {String(recipe.episode).padStart(2, "0")}
                            </span>
                            <span className="w-[3px] h-[3px] rounded-full bg-[#E8D9B5]/60" />
                            <span className="text-[10px] uppercase tracking-[0.28em] text-hl-bg/85 leading-none">
                                Taste the Story
                            </span>
                        </div>
                    ) : null}
                    <h1
                        data-testid="recipe-title"
                        className="font-serif text-hl-bg text-5xl md:text-7xl lg:text-[6rem] leading-[0.98] max-w-4xl"
                    >
                        {recipe.title}
                    </h1>
                    <div className="mt-8 flex flex-wrap gap-5 md:gap-7 text-hl-bg/85 text-[11px] uppercase tracking-[0.25em]">
                        <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {recipe.origin}</span>
                        <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {total} min total</span>
                        <span className="flex items-center gap-2"><Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Serves {recipe.servings}</span>
                        <span className="flex items-center gap-2"><Flame className="w-3.5 h-3.5" strokeWidth={1.5} /> {recipe.difficulty}</span>
                    </div>
                    {(recipe.categories || []).length > 0 ? (
                        <div className="mt-5 flex flex-wrap gap-2">
                            {recipe.categories.map((c) => (
                                <Link
                                    key={c}
                                    to={`/recipes?category=${encodeURIComponent(c)}`}
                                    className="px-3 py-1 text-[10px] uppercase tracking-[0.25em] rounded-full border border-hl-bg/40 text-hl-bg/85 hover:bg-hl-bg/10 transition-colors"
                                >
                                    {c}
                                </Link>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* BACKSTORY */}
            <section
                data-testid="recipe-backstory-section"
                className="max-w-4xl mx-auto px-6 lg:px-10 pt-24 pb-12"
            >
                <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
                    <div className="hl-divider max-w-xs flex-1 min-w-[180px]">
                        <span className="font-script text-2xl text-hl-secondary">the history —</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="hidden sm:block text-[10px] uppercase tracking-[0.28em] text-hl-muted">
                            Share this dish
                        </span>
                        <ShareButtons title={recipe.title} />
                    </div>
                </div>
                <h2 className="font-serif text-3xl md:text-5xl text-hl-brown mt-1 mb-10 leading-tight">
                    How this dish <span className="italic">came to life</span>
                </h2>
                <div
                    data-testid="recipe-backstory"
                    className="hl-dropcap font-serif italic text-hl-text/90 text-xl md:text-2xl leading-[1.65] whitespace-pre-wrap"
                >
                    {recipe.backstory}
                </div>

                {recipe.cultural_significance ? (
                    <div
                        data-testid="recipe-cultural-significance"
                        className="mt-12 p-8 md:p-10 bg-hl-tinted/60 border-l-4 border-hl-secondary rounded-sm"
                    >
                        <p className="text-[11px] uppercase tracking-[0.3em] text-hl-secondary mb-3">
                            Cultural significance
                        </p>
                        <p className="font-serif text-xl md:text-2xl text-hl-brown leading-relaxed">
                            {recipe.cultural_significance}
                        </p>
                    </div>
                ) : null}

                {/* Aphrodite's Roots — dynamic per-product callouts */}
                {(() => {
                    const featured = (recipe.featured_products || [])
                        .map((pid) => products.find((p) => p.id === pid))
                        .filter(Boolean);
                    if (featured.length === 0) return null;
                    return (
                        <div className="mt-12 space-y-6">
                            {featured.map((prod) => (
                                <a
                                    key={prod.id}
                                    href="https://aphroditeroots.com/?utm_source=icookgreek&utm_medium=ebook&utm_campaign=ebook_showcase&utm_content=recipe_callout"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`recipe-pantry-callout-${prod.id}`}
                                    className="group block p-6 md:p-8 bg-hl-brown text-hl-bg rounded-sm hl-shadow-warm hl-grain overflow-hidden relative"
                                >
                                    <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-center relative z-10">
                                        <div className="hidden md:block w-24 h-24 lg:w-28 lg:h-28 rounded-sm overflow-hidden bg-[#E8D9B5]/10 shrink-0">
                                            <img
                                                src={prod.image}
                                                alt={prod.name}
                                                loading="lazy"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-script text-2xl text-[#E8D9B5]">
                                                {prod.callout_kicker || "from our pantry —"}
                                            </p>
                                            <p className="mt-2 font-serif text-2xl md:text-3xl leading-tight">
                                                {prod.callout_headline || `The ${prod.name.toLowerCase()} in this recipe is the one we're bottling.`}
                                            </p>
                                            <p className="mt-3 text-hl-bg/75 text-sm md:text-base leading-relaxed">
                                                {prod.callout_body}
                                            </p>
                                        </div>
                                        <div className="flex md:items-center md:justify-end">
                                            <span className="inline-flex items-center gap-3 px-6 py-3 border border-[#E8D9B5]/60 group-hover:bg-[#E8D9B5]/10 transition-colors text-[11px] uppercase tracking-[0.3em] text-[#E8D9B5] whitespace-nowrap">
                                                Preorder now
                                                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            </span>
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    );
                })()}

                <div className="hl-divider mt-16">
                    <span className="font-script text-2xl text-hl-primary">now let's cook</span>
                </div>
            </section>

            {/* INGREDIENTS + INSTRUCTIONS */}
            {(
                <section className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-5 gap-14 mt-6">
                    <aside
                        data-testid="recipe-ingredients"
                        className="md:col-span-2 md:sticky md:top-24 self-start bg-hl-surface rounded-sm p-8 md:p-10 hl-shadow-warm hl-grain"
                    >
                        <p className="font-script text-2xl text-hl-secondary mb-1">what you need</p>
                        <h3 className="font-serif text-3xl text-hl-brown mb-6">Ingredients</h3>
                        <ul className="space-y-3">
                            {recipe.ingredients.map((ing, i) => (
                                <li
                                    key={i}
                                    data-testid={`ingredient-${i}`}
                                    className="flex gap-3 text-hl-text leading-relaxed"
                                >
                                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-hl-secondary shrink-0" />
                                    <span>{ing}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>

                    <div data-testid="recipe-instructions" className="md:col-span-3">
                        <p className="font-script text-2xl text-hl-secondary mb-1">step by step</p>
                        <h3 className="font-serif text-3xl text-hl-brown mb-10">Instructions</h3>
                        <ol className="space-y-7">
                            {recipe.instructions.map((step, i) => (
                                <li
                                    key={i}
                                    data-testid={`instruction-${i}`}
                                    className="flex gap-5"
                                >
                                    <span className="shrink-0 w-11 h-11 rounded-full bg-hl-primary text-hl-bg flex items-center justify-center font-serif text-xl">
                                        {i + 1}
                                    </span>
                                    <p className="text-hl-text text-lg leading-relaxed pt-1.5">
                                        {step}
                                    </p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            {/* TIPS — only for unlocked */}
            {(recipe.tips || []).length > 0 ? (
                <section
                    data-testid="recipe-tips"
                    className="max-w-4xl mx-auto px-6 lg:px-10 mt-24"
                >
                    <div className="bg-hl-brown text-hl-bg rounded-sm p-10 md:p-14 hl-shadow-deep">
                        <div className="flex items-center gap-3 mb-2">
                            <Lightbulb className="w-5 h-5 text-[#E8D9B5]" strokeWidth={1.5} />
                            <p className="font-script text-2xl text-[#E8D9B5]">tips from the village —</p>
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl mb-8 leading-tight">
                            Wisdom from grandmothers
                        </h3>
                        <ul className="space-y-5">
                            {recipe.tips.map((t, i) => (
                                <li
                                    key={i}
                                    data-testid={`tip-${i}`}
                                    className="flex gap-4 text-lg leading-relaxed text-hl-bg/90"
                                >
                                    <span className="font-serif text-2xl text-hl-secondary leading-none">
                                        {(i + 1).toString().padStart(2, "0")}
                                    </span>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            ) : null}

            {/* VIDEO section removed — no videos on recipes anymore */}

            {/* RELATED */}
            {related.length > 0 ? (
                <section
                    data-testid="recipe-related"
                    className="max-w-7xl mx-auto px-6 lg:px-10 mt-32"
                >
                    <div className="hl-divider mb-6 max-w-md">
                        <span className="font-script text-2xl text-hl-secondary">keep cooking —</span>
                    </div>
                    <h3 className="font-serif text-3xl md:text-5xl text-hl-brown mb-12 leading-tight">
                        Related <span className="italic">recipes</span>
                    </h3>
                    <div className="grid gap-10 md:grid-cols-3">
                        {related.map((r, i) => (
                            <RecipeCard key={r.id} recipe={r} index={i} />
                        ))}
                    </div>
                </section>
            ) : null}

            {/* CTA strip */}
            <section className="mt-32 max-w-7xl mx-auto px-6 lg:px-10">
                <div className="rounded-sm overflow-hidden bg-hl-tinted/70 border border-hl-border p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between hl-grain">
                    <div>
                        <p className="font-script text-3xl text-hl-secondary leading-none">καλή όρεξη —</p>
                        <p className="font-serif text-3xl md:text-4xl mt-2 text-hl-brown">
                            Cook it slowly. Share it with someone you love.
                        </p>
                        <div className="mt-6">
                            <ShareButtons title={recipe.title} />
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <Link
                            to="/recipes"
                            className="px-7 py-3.5 bg-hl-brown text-hl-bg hover:bg-hl-text hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                        >
                            More recipes
                        </Link>
                        <Link
                            to="/aphrodites-roots"
                            className="px-7 py-3.5 border border-hl-brown text-hl-brown hover:bg-hl-brown hover:text-hl-bg hl-btn-lift text-[11px] uppercase tracking-[0.3em]"
                        >
                            <BookOpen className="w-3.5 h-3.5 inline mr-2" strokeWidth={1.5} /> Pantry
                        </Link>
                    </div>
                </div>
            </section>
        </article>
    );
};

export default RecipeDetail;
